import { useState, useCallback, useEffect } from 'react';
import { COLORS, INPUT_TOKENS, DETAIL_TOKENS } from '../../../styles/UISystem';
import { IconCircleButton } from '../Icons/IconCircleButton';
import { Plus } from '@phosphor-icons/react/dist/ssr/Plus';
import ContactField from './ContactField';
import AddContactOverlay from './AddContactOverlay';
import ContactTypePickerOverlay from './ContactTypePickerOverlay';
import AddCircleButton from '../AddCircleButton';

/**
 * AddContactRow — "Add more" as a ROW, matching the contact rows exactly
 * (own-profile frames, 2026-08-18): same pill, the + in the icon slot, the
 * label where a value would sit. It reads as the next empty row rather than a
 * button parked beside the list.
 *
 * The + is quiet while an empty starter row is still showing and filled once
 * every row has a value — the frames show both, and `emphasized` already
 * carried that distinction.
 */
const AddContactRow = ({ label, emphasized, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      marginBottom: '10px',
      width: DETAIL_TOKENS.ROW_WIDTH,
      maxWidth: DETAIL_TOKENS.ROW_MAX_WIDTH,
      minWidth: DETAIL_TOKENS.ROW_MIN_WIDTH,
      marginLeft: 'auto',
      marginRight: 'auto',
      display: 'flex',
      alignItems: 'center',
      padding: '8px 16px',
      borderRadius: INPUT_TOKENS.CONTACT_RADIUS,
      border: INPUT_TOKENS.CONTACT_BORDER,
      backgroundColor: INPUT_TOKENS.CONTACT_BG_EDITABLE,
      boxSizing: 'border-box',
      cursor: 'pointer',
      WebkitTapHighlightColor: 'transparent',
    }}
  >
    <span
      style={{
        width: '36px',
        marginRight: '8px',
        display: 'flex',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <IconCircleButton
        icon={Plus}
        buttonSize={36}
        size={18}
        inline
        backgroundColor={emphasized ? COLORS.DARK_GRAY : 'transparent'}
        iconColor={emphasized ? '#FFFFFF' : '#C7C7CC'}
        iconWeight={emphasized ? 'bold' : 'regular'}
      />
    </span>
    {/* nowrap: the 36px icon slot and the 44px mirror below leave the label
        ~160px inside a 280px pill, and "Add more socials" is a hair wider than
        that at 17px — it wrapped to two lines on the 2026-08-20 device pass.
        The row is unchanged in width; the copy simply outgrew the gap. */}
    <span
      style={{
        flex: 1,
        textAlign: 'center',
        fontSize: '17px',
        color: COLORS.MEDIUM_GRAY,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
    {/* Mirrors the icon slot so the label centres in the pill, not in the
        space the icon leaves over — same rule the contact rows follow. */}
    <span style={{ width: '44px', flexShrink: 0 }} aria-hidden="true" />
  </button>
);

/**
 * Types that support max 2 entries (primary + secondary)
 */
const MAX_TWO_TYPES = ['phone', 'email', 'instagram', 'whatsapp'];

/**
 * Primary contact types (shown first in picker)
 */
const PRIMARY_TYPES = ['phone', 'email', 'instagram', 'linkedin', 'whatsapp'];

/**
 * Secondary contact types (shown after divider, alphabetically)
 */
const SECONDARY_TYPES = [
  'facebook',
  'github',
  'signal',
  'snapchat',
  'substack',
  'telegram',
  'tiktok',
  'twitch',
  'twitter',
  'youtube',
  'custom',
];

/**
 * Get all possible field keys for a base type
 * e.g., 'phone' -> ['phone', 'phone2']
 */
const getFieldKeys = (baseType) => {
  if (MAX_TWO_TYPES.includes(baseType)) {
    return [baseType, `${baseType}2`];
  }
  return [baseType];
};

/**
 * Check if a type is available to add (not already at max)
 */
const isTypeAvailable = (baseType, userData) => {
  const fieldKeys = getFieldKeys(baseType);
  const filledCount = fieldKeys.filter((key) => userData[key]).length;
  return filledCount < fieldKeys.length;
};

/**
 * Get the next available field key for a type
 * e.g., if 'phone' exists, return 'phone2'
 */
const getNextFieldKey = (baseType, userData) => {
  const fieldKeys = getFieldKeys(baseType);
  for (const key of fieldKeys) {
    if (!userData[key]) {
      return key;
    }
  }
  return null; // All slots filled
};

/**
 * ContactFields Component
 *
 * Composite component that renders multiple ContactField instances from a userData object.
 * Handles progressive display, max-2 logic, type picker, and field promotion.
 *
 * @param {object} userData - Object with phone, phone2, email, etc.
 * @param {boolean} disabled - Controls all fields
 * @param {boolean} showLinks - Context: detail page vs form
 * @param {function} onChange - Called with ONE patch object, `{ [fieldKey]: value }`.
 *   A patch, not `(fieldKey, value)`, because a promotion changes two fields at
 *   once and had to be deliverable atomically — see handleFieldClear. Consumers
 *   merge it into their record; none of them may rebuild from a stale closure.
 * @param {array} starterFields - Fields shown for new users (default: phone, email, instagram, linkedin)
 * @param {boolean} hideAddButton - If true, hides the "Add contact" button (e.g., for ProfileForm)
 * @param {boolean} alwaysShowStarters - If true, always show starterFields (for ProfileForm edit mode)
 * @param {boolean} compact - Detail-page rows: narrower than the page's sections, value centred
 * @param {object} addRef - Optional ref the component fills with its "open the
 *   type picker" opener, so a page can place the add affordance itself. Same
 *   shape as InlineSharingList's commitRef. Pair with hideAddButton.
 */
const ContactFields = ({
  userData = {},
  disabled = false,
  showLinks = false,
  onChange,
  starterFields = ['phone', 'email', 'instagram', 'linkedin'],
  hideAddButton = false,
  alwaysShowStarters = false,
  readOnlyFields = [],
  addLabel = 'Add contact',
  addEmphasized = false,
  compact = false,
  addRef = null,
}) => {
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [overlayConfig, setOverlayConfig] = useState(null); // { type, fieldKey } when overlay is open

  // Hand the picker opener to the page when it wants to own placement — the
  // 2026-08-18 frames put the + below "Save to contacts", outside this list.
  useEffect(() => {
    if (addRef) addRef.current = () => setShowTypePicker(true);
  }, [addRef]);

  // Determine which fields to display
  const getDisplayFields = useCallback(() => {
    const fields = [];
    const addedFieldKeys = new Set();

    // Check if user has any contact fields with values (returning user)
    const hasAnyFieldsWithValues = Object.keys(userData).some((key) => {
      const isContactField =
        [...PRIMARY_TYPES, ...SECONDARY_TYPES].includes(key) ||
        [...PRIMARY_TYPES, ...SECONDARY_TYPES].some((t) => key === `${t}2`);
      return isContactField && userData[key]; // Has a truthy value
    });

    if (alwaysShowStarters) {
      // Creation/edit flow: show filled fields first, then empty starters after
      // This ensures pre-filled data (e.g. from exchange) appears at the top
      for (const type of PRIMARY_TYPES) {
        if (userData[type] && !addedFieldKeys.has(type)) {
          fields.push({ type, fieldKey: type });
          addedFieldKeys.add(type);
        }
        if (
          MAX_TWO_TYPES.includes(type) &&
          userData[`${type}2`] &&
          !addedFieldKeys.has(`${type}2`)
        ) {
          fields.push({ type, fieldKey: `${type}2` });
          addedFieldKeys.add(`${type}2`);
        }
      }
      for (const type of SECONDARY_TYPES) {
        if (userData[type] && !addedFieldKeys.has(type)) {
          fields.push({ type, fieldKey: type });
          addedFieldKeys.add(type);
        }
      }
      // Then add empty starter fields
      for (const fieldKey of starterFields) {
        const type = fieldKey.replace(/\d+$/, '');
        if (!addedFieldKeys.has(fieldKey)) {
          fields.push({ type, fieldKey });
          addedFieldKeys.add(fieldKey);
        }
      }
    } else if (hasAnyFieldsWithValues) {
      // Returning user: show fields that have values
      for (const type of PRIMARY_TYPES) {
        if (userData[type] && !addedFieldKeys.has(type)) {
          fields.push({ type, fieldKey: type });
          addedFieldKeys.add(type);
        }
        if (
          MAX_TWO_TYPES.includes(type) &&
          userData[`${type}2`] &&
          !addedFieldKeys.has(`${type}2`)
        ) {
          fields.push({ type, fieldKey: `${type}2` });
          addedFieldKeys.add(`${type}2`);
        }
      }
      for (const type of SECONDARY_TYPES) {
        if (userData[type] && !addedFieldKeys.has(type)) {
          fields.push({ type, fieldKey: type });
          addedFieldKeys.add(type);
        }
      }
    } else {
      // New user: show starter fields (empty)
      for (const fieldKey of starterFields) {
        const type = fieldKey.replace(/\d+$/, '');
        if (!addedFieldKeys.has(fieldKey)) {
          fields.push({ type, fieldKey });
          addedFieldKeys.add(fieldKey);
        }
      }
    }

    return fields;
  }, [userData, starterFields, alwaysShowStarters]);

  // Handle field value change
  const handleFieldChange = useCallback(
    (fieldKey, value) => {
      onChange?.({ [fieldKey]: value });
    },
    [onChange]
  );

  // Handle field clear (implements field promotion)
  const handleFieldClear = useCallback(
    (fieldKey) => {
      const baseType = fieldKey.replace(/\d+$/, '');

      // Check if this is a primary field with a secondary value
      if (!fieldKey.endsWith('2') && MAX_TWO_TYPES.includes(baseType)) {
        const secondaryKey = `${baseType}2`;
        const secondaryValue = userData[secondaryKey];

        if (secondaryValue) {
          // Promote secondary to primary in ONE call. Two sequential calls
          // raced every consumer that rebuilds from a prop closure: both reads
          // saw the same pre-promotion record, so the second overwrote the
          // first and the secondary value was destroyed (verified on
          // ProfileForm at /go:review, 2026-08-20 — clearing a primary phone
          // deleted the second one and left the first in place). A page-level
          // ref papered over it on one of four consumers; an atomic patch fixes
          // all of them and needs no shadow copy of DB state.
          onChange?.({ [fieldKey]: secondaryValue, [secondaryKey]: '' });
          return;
        }
      }

      // Normal clear
      onChange?.({ [fieldKey]: '' });
    },
    [userData, onChange]
  );

  // Handle adding a new contact type - opens input overlay
  const handleAddType = (baseType) => {
    const fieldKey = getNextFieldKey(baseType, userData);
    if (fieldKey) {
      setOverlayConfig({ type: baseType, fieldKey });
    }
  };

  // Handle overlay save
  const handleOverlaySave = (fieldKey, value) => {
    onChange?.({ [fieldKey]: value });
  };

  // Get available types for the picker
  const getAvailableTypes = useCallback(() => {
    const available = {
      primary: [],
      secondary: [],
    };

    for (const type of PRIMARY_TYPES) {
      if (isTypeAvailable(type, userData)) {
        available.primary.push(type);
      }
    }

    for (const type of SECONDARY_TYPES) {
      if (isTypeAvailable(type, userData)) {
        available.secondary.push(type);
      }
    }

    return available;
  }, [userData]);

  const displayFields = getDisplayFields();
  const availableTypes = getAvailableTypes();
  const hasAvailableTypes =
    availableTypes.primary.length > 0 || availableTypes.secondary.length > 0;

  return (
    <div>
      {/* Render contact fields */}
      {displayFields.map(({ type, fieldKey }) => (
        <ContactField
          key={fieldKey}
          type={type}
          value={userData[fieldKey] || ''}
          fieldKey={fieldKey}
          // readOnlyFields matches the specific field key (e.g. 'email'), so a peer's
          // shared email doesn't lock a user-added second email ('email2').
          disabled={disabled || readOnlyFields.includes(fieldKey)}
          showLinks={showLinks}
          compact={compact}
          onChange={(value) => handleFieldChange(fieldKey, value)}
          onClear={() => handleFieldClear(fieldKey)}
        />
      ))}

      {/* Add contact. On a detail-page column it's a ROW matching the contacts
          (frames); elsewhere it stays the standard circle-plus-label. */}
      {!disabled &&
        !hideAddButton &&
        hasAvailableTypes &&
        (compact ? (
          <AddContactRow
            label={addLabel}
            emphasized={addEmphasized}
            onClick={() => setShowTypePicker(true)}
          />
        ) : (
          <div>
            <AddCircleButton
              label={addLabel}
              emphasized={addEmphasized}
              onClick={() => setShowTypePicker(true)}
              style={{ paddingLeft: '16px' }}
            />
          </div>
        ))}

      {/* Contact type picker overlay */}
      <ContactTypePickerOverlay
        isOpen={showTypePicker}
        availableTypes={availableTypes}
        onSelect={handleAddType}
        onClose={() => setShowTypePicker(false)}
      />

      {/* Add contact overlay */}
      <AddContactOverlay
        isOpen={!!overlayConfig}
        type={overlayConfig?.type}
        fieldKey={overlayConfig?.fieldKey}
        onSave={handleOverlaySave}
        onClose={() => setOverlayConfig(null)}
      />
    </div>
  );
};

/**
 * ContactFieldsExample - For UI Showcase
 */
export const ContactFieldsExample = () => {
  // Simulate a returning user with some data
  const [userData, setUserData] = useState({
    phone: '+19176502256',
    email: 'bob@email.com',
    instagram: 'bobmcgoo',
  });

  // Simulate a new user (no data)
  const [newUserData, setNewUserData] = useState({});

  const handleChange = (setter) => (fieldKey, value) => {
    // Keep empty values as empty strings - field stays visible until explicit removal
    setter((prev) => ({ ...prev, [fieldKey]: value }));
  };

  return (
    <div style={{ padding: '20px', maxWidth: '400px', margin: '0 auto' }}>
      <h2 style={{ color: COLORS.DARK_GRAY, marginBottom: '8px', textAlign: 'center' }}>
        ContactFields
      </h2>
      <p
        style={{
          color: COLORS.MEDIUM_GRAY,
          marginBottom: '24px',
          textAlign: 'center',
          fontSize: '14px',
        }}
      >
        Composite component rendering multiple ContactField instances with &ldquo;Add contact&rdquo;
        picker.
      </p>

      {/* Returning User - Editable */}
      <h3
        style={{
          fontSize: '16px',
          fontWeight: '600',
          color: COLORS.DARK_GRAY,
          marginBottom: '12px',
        }}
      >
        Returning User (Editable)
      </h3>
      <p style={{ fontSize: '12px', color: COLORS.MEDIUM_GRAY, marginBottom: '12px' }}>
        Shows existing fields (cleared fields stay visible) + Add contact button
      </p>

      <ContactFields
        userData={userData}
        disabled={false}
        showLinks={false}
        onChange={handleChange(setUserData)}
      />

      {/* New User - Editable */}
      <h3
        style={{
          fontSize: '16px',
          fontWeight: '600',
          color: COLORS.DARK_GRAY,
          marginTop: '32px',
          marginBottom: '12px',
        }}
      >
        New User (Editable)
      </h3>
      <p style={{ fontSize: '12px', color: COLORS.MEDIUM_GRAY, marginBottom: '12px' }}>
        Shows starter fields (Phone, Email, Instagram, LinkedIn) + Add contact button
      </p>

      <ContactFields
        userData={newUserData}
        disabled={false}
        showLinks={false}
        onChange={handleChange(setNewUserData)}
      />

      {/* Read-only with Links */}
      <h3
        style={{
          fontSize: '16px',
          fontWeight: '600',
          color: COLORS.DARK_GRAY,
          marginTop: '32px',
          marginBottom: '12px',
        }}
      >
        Read-only with Links (Detail Page)
      </h3>
      <p style={{ fontSize: '12px', color: COLORS.MEDIUM_GRAY, marginBottom: '12px' }}>
        disabled=true, showLinks=true - Icons are clickable
      </p>

      <ContactFields
        userData={{
          phone: '+19176502256',
          phone2: '+14155551234',
          email: 'bob@email.com',
          instagram: 'bobmcgoo',
          linkedin: 'bobmcgoo',
        }}
        disabled={true}
        showLinks={true}
        onChange={() => {}}
      />

      {/* Values display */}
      <div
        style={{
          marginTop: '24px',
          padding: '12px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          fontSize: '12px',
        }}
      >
        <strong>Returning User Data:</strong>
        <pre style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(userData, null, 2)}
        </pre>
      </div>

      <div
        style={{
          marginTop: '12px',
          padding: '12px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          fontSize: '12px',
        }}
      >
        <strong>New User Data:</strong>
        <pre style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(newUserData, null, 2)}
        </pre>
      </div>
    </div>
  );
};

export default ContactFields;
